import { Box, Typography } from '@mui/material';
import { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Button from '@Components/Button';
import MainSectionLayout from '@Components/MainSectionLayout';
import { MenuItem } from '@Components/PopOverMenu';
import ScrollToTop from '@Components/ScrollToTop';
import Table, { TableHead } from '@Components/Table';
import CommonConstants from '@Constants/common.constants';
import { getHyphenSeperatedDateMMDDYYYYHHMM } from '@Containers/Report/ActiveSessions/ActiveSessions.utils';
import { HeaderCheckboxState, Icon } from '@Typings/global.types';
import {
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

import ActiveSessionsTableProps from './ActiveSessionsTable.types';

const ActiveSessionsTable: FunctionComponent<ActiveSessionsTableProps> = (
  props: ActiveSessionsTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** PROPS */
  const {
    tableProps,
    handleBulk,
    cancelBtnProps,
    selectedBtnNumber,
    selectedBtnProps,
    refreshBtnProps,
    endSessionBtnProps,
    lastUpdated,
    isBleedOutActive,
    ...resProps
  } = props;

  /** TABLE PROPS */
  const {
    loading,
    rows,
    paginationProps,
    setChecked,
    checked,
    ...resTableProps
  } = tableProps;

  /** PAGINATION PROPS */
  const { count, ...resPaginationProps } = paginationProps;

  /** STATES */
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [headerCheckboxState, setHeaderCheckboxState] =
    useState<HeaderCheckboxState>({ checked: false, indeterminate: false });

  /** Page Heading */
  const headingText = t('reports.Active Sessions');

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    /** SET STATES */
    setPage(pageNo);
    setRowsPerPage(pageSize);
  }, [setPage, setRowsPerPage]);

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  /** EDGE CASE
   * If the user performs an action and there are no more results to show
   * on the particular page then the user is redirected to the previous valid page.
   *
   * Set the pageNo to the previous valid page.
   */
  useEffect(() => {
    const newPageNo = Math.ceil(count / rowsPerPage);

    if (count && newPageNo < page + 1) {
      const pageNoParam = query.set(
        CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
        newPageNo
      );

      changeRoute(pageNoParam);
    }
  }, [count, page, rowsPerPage, changeRoute]);

  /** PAGINATION HANDLERS --> */
  /** ROWS PER PAGE OPTIONS */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    count
  );

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );

    changeRoute(pageParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });

    changeRoute(pageParams);
  };
  /** <-- PAGINATION HANDLERS */

  /** TABLE HEADER CHECKBOX HANDLERS --> */
  /** Clears all checkboxes. */
  const clearSelection = (): void => {
    setHeaderCheckboxState({ checked: false, indeterminate: false });
    if (handleBulk) {
      handleBulk(0);
    }
    if (setChecked) {
      setChecked([]);
    }
  };

  /** Selects the checkboxes of current page. */
  const selectPage = (): void => {
    setHeaderCheckboxState({ checked: true, indeterminate: false });
    if (setChecked) {
      setChecked(rows.map((row) => row.id));
    }
  };

  /** Selects the first 1000 rows
   * Select all rows if less than 1000
   */
  const select1000 = () => {
    clearSelection();
    if (handleBulk) {
      handleBulk(Math.min(count, CommonConstants.BULK_COUNT));
    }
  };

  /** Table Head Checkbox Handler */
  const headerCheckClickHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked && !headerCheckboxState.indeterminate) {
      selectPage();
    } else {
      clearSelection();
    }
  };

  /** HANDLES THE TABLE HEADER CHECKBOX STATE (checked and indeterminate) */
  useEffect(() => {
    if (!loading) {
      if (checked?.length && checked.length !== rows.length) {
        setHeaderCheckboxState({ checked: false, indeterminate: true });
      } else if (checked?.length && checked.length === rows.length) {
        setHeaderCheckboxState({ checked: true, indeterminate: false });
      } else {
        setHeaderCheckboxState({ checked: false, indeterminate: false });
      }
    }
  }, [checked?.length, rows.length, loading]);

  /** TABLE HEAD CHECKBOX MENU OPTIONS */
  const menuOptions: MenuItem[] = [
    {
      label: t('common.table.Select Page'),
      value: 'page',
      onClick: selectPage,
    },
    {
      label: t('common.table.Select first 1000'),
      value: '1000',
      onClick: select1000,
    },
    {
      label: t('common.table.Clear Selection'),
      value: 'clear',
      onClick: clearSelection,
    },
  ];
  /** <-- TABLE HEADER CHECKBOX HANDLERS */

  /** TABLE --> */
  /** TABLE HEAD */
  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.ORDER
    ) as string;
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]: order,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    return [
      {
        label: t('reports.Account Name'),
        icon: getTableHeadIcon(CommonConstants.QUERY_PARAMS.COMMON.NAME),
        width: 25.6,
        onClick: () =>
          handleTableHeadClick(CommonConstants.QUERY_PARAMS.COMMON.NAME),
      },
      {
        label: t('reports.Start Date'),
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.START_DATE
        ),
        width: 24,
        align: 'right',
        onClick: () =>
          handleTableHeadClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.START_DATE
          ),
      },
      {
        label: t('reports.Session Duration'),
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DURATION
        ),
        width: 18.8,
        align: 'right',
        onClick: () =>
          handleTableHeadClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.SESSION_DURATION
          ),
      },
      {
        label: t('reports.Last Call'),
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.LAST_CALL
        ),
        width: 24.4,
        align: 'center',
        onClick: () =>
          handleTableHeadClick(
            CommonConstants.QUERY_PARAMS.REPORT.SORT_BY.LAST_CALL
          ),
      },
      {
        label: t('common.table.Actions'),
        width: 13.6,
        align: 'center',
      },
    ];
  };

  /** ACTIVE SESSION TABLE COMPONENT */
  const TableComponent = (
    <Table
      loading={loading}
      rows={rows}
      tableHead={getTableHead()}
      checked={checked}
      setChecked={setChecked}
      checkboxMenuProps={{
        menuOptions,
        checkboxProps: {
          checked: headerCheckboxState.checked,
          indeterminate: headerCheckboxState.indeterminate,
          onChange: headerCheckClickHandler,
          disabled: !rows.length,
        },
      }}
      paginationProps={{
        count,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...resPaginationProps,
      }}
      {...resTableProps}
    />
  );
  /** <-- TABLE */

  const filtersComponent = (
    <>
      <Box
        display="flex"
        justifyContent={isBleedOutActive ? 'space-between' : 'flex-end'}
        alignItems="center"
      >
        {isBleedOutActive && (
          <Typography variant="main">
            {t('reports.Bleed out mode is active')}
          </Typography>
        )}
        <Box display="flex" justifyContent="flex-end">
          <Box mr={1.6} textAlign="right">
            <Typography variant="main" sx={{ color: 'text.secondary' }}>
              {t('reports.Last Updated')}
            </Typography>
            <Typography variant="main" sx={{ color: 'text.secondary' }}>
              {lastUpdated
                ? getHyphenSeperatedDateMMDDYYYYHHMM(lastUpdated)
                : t('reports.never')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            icon="refresh"
            color="secondary"
            text={t('reports.Refresh')}
            sx={{ mr: 1.6 }}
            {...refreshBtnProps}
          />
        </Box>
      </Box>
      {!!selectedBtnNumber && (
        <Box display="inline-flex" alignItems="center" gap={0.8} mt={1.6}>
          <Button
            variant="text"
            color="inherit"
            icon="checkboxFilled"
            iconColor="primary"
            text={`${selectedBtnNumber || 0} ${t('common.messages.Selected')}`}
            sx={{ textTransform: 'none' }}
            onClick={() => clearSelection()}
            {...selectedBtnProps}
          />
          <Button
            variant="text"
            color="inherit"
            icon="block"
            iconColor="inherit"
            text={t('reports.End')}
            sx={{ textTransform: 'none' }}
            {...endSessionBtnProps}
          />
          <Button
            variant="text"
            color="secondary"
            text={t('common.messages.Cancel')}
            onClick={() => clearSelection()}
            {...cancelBtnProps}
          />
        </Box>
      )}
    </>
  );

  return (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <MainSectionLayout
        table={TableComponent}
        heading={headingText}
        filters={filtersComponent}
        {...resProps}
      />
    </>
  );
};

export default ActiveSessionsTable;
